import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Copy, Share2, Users, Wallet, Loader2, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const fmtRp = (n) => "Rp " + (parseInt(n || 0, 10) || 0).toLocaleString("id-ID");

/**
 * ReferralCard — shown on the pilgrim's My Bookings page.
 * Displays code, share url, referred count, credit balance.
 */
export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemForm, setRedeemForm] = useState({ amount: "", bank_name: "", bank_account_number: "", bank_account_name: "", notes: "" });
  const [redeeming, setRedeeming] = useState(false);

  const load = () => {
    api.get("/referral/me")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submitRedeem = async (e) => {
    e.preventDefault();
    const amt = parseInt(String(redeemForm.amount).replace(/\D/g, ""), 10);
    if (!amt || amt < 100_000) { toast.error("Minimal pencairan Rp 100.000"); return; }
    if (amt % 50_000 !== 0) { toast.error("Nominal harus kelipatan Rp 50.000"); return; }
    if (amt > (data?.credit_balance || 0)) { toast.error("Saldo tidak cukup"); return; }
    setRedeeming(true);
    try {
      await api.post("/referral/redeem", { ...redeemForm, amount: amt });
      toast.success("Permintaan pencairan terkirim ke admin");
      setRedeemOpen(false);
      setRedeemForm({ amount: "", bank_name: "", bank_account_number: "", bank_account_name: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim permintaan");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/10 to-transparent p-6 flex items-center gap-3">
        <Loader2 className="h-4 w-4 animate-spin text-accent" />
        <span className="text-sm text-muted-foreground">Memuat kode referral…</span>
      </div>
    );
  }
  if (!data) return null;

  const shareText = `Assalamualaikum 🙏\n\nSaya rekomendasikan Batik Tour & Travel untuk Umrah — travel resmi PPIU/PIHK dengan pelayanan yang berkesan.\n\nGunakan kode referral saya untuk potongan Rp ${data.discount_amount.toLocaleString("id-ID")}:\n\n🎁 Kode: ${data.code}\n🔗 ${data.share_url}\n\nBarakallahu fiik 🌙`;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Tersalin ke clipboard");
    } catch { toast.error("Gagal menyalin"); }
  };

  const shareWA = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank");
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-[#1a1a1a] via-primary to-[#2a1a05] text-white p-6 lg:p-8"
      data-testid="referral-card"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 text-accent">
          <Gift className="h-4 w-4" />
          <p className="text-xs uppercase tracking-[0.25em]">Ajak Kerabat &amp; Dapatkan Bonus</p>
        </div>
        <h3 className="mt-3 font-serif text-2xl lg:text-3xl leading-tight">
          Setiap teman yang umrah dengan kode Anda, Anda dapat{" "}
          <span className="text-accent">Rp {(data.reward_amount / 1000).toFixed(0)}rb</span>
        </h3>
        <p className="mt-2 text-white/70 text-sm">
          Teman Anda hemat Rp {data.discount_amount.toLocaleString("id-ID")}. Bonus otomatis masuk ke saldo Anda saat booking teman dikonfirmasi admin.
        </p>

        {/* Referral code display */}
        <div className="mt-6 flex items-center gap-3 bg-black/40 border border-accent/40 rounded-xl p-4">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-white/60">Kode Anda</p>
            <p className="mt-1 font-mono text-2xl text-accent tracking-wider" data-testid="referral-code">{data.code}</p>
          </div>
          <button
            onClick={() => copy(data.code)}
            data-testid="copy-code"
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            title="Salin kode"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>

        {/* Stats row */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-white/60"><Users className="h-3 w-3" /><span className="text-[10px] uppercase tracking-wider">Diajak</span></div>
            <p className="mt-1 font-serif text-xl text-white">{data.referred_count}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-white/60"><Wallet className="h-3 w-3" /><span className="text-[10px] uppercase tracking-wider">Saldo</span></div>
            <p className="mt-1 font-serif text-xl text-accent">{fmtRp(data.credit_balance)}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-white/60"><Gift className="h-3 w-3" /><span className="text-[10px] uppercase tracking-wider">Terkumpul</span></div>
            <p className="mt-1 font-serif text-xl text-white">{fmtRp(data.credit_earned)}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            onClick={shareWA}
            data-testid="share-wa"
            className="inline-flex items-center gap-2 bg-accent text-primary hover:bg-accent/90"
          >
            <Share2 className="h-4 w-4" /> Bagikan via WhatsApp
          </Button>
          <button
            onClick={() => copy(data.share_url)}
            data-testid="copy-url"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/30 text-sm text-white hover:bg-white/10 transition-colors"
          >
            <Copy className="h-3.5 w-3.5" /> Salin Link
          </button>
          <button
            onClick={() => setRedeemOpen(true)}
            disabled={(data.credit_balance || 0) < 100_000}
            data-testid="redeem-btn"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md border border-accent bg-accent/20 text-sm text-accent hover:bg-accent/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Wallet className="h-3.5 w-3.5" /> Cairkan Saldo
          </button>
        </div>

        {(data.credit_pending || 0) > 0 && (
          <p className="mt-3 text-xs text-yellow-400" data-testid="pending-notice">
            ⏳ Pencairan {fmtRp(data.credit_pending)} sedang diproses admin.
          </p>
        )}

        <p className="mt-4 text-xs text-white/50">
          * Saldo dapat dicairkan ke rekening bank Anda. Minimal Rp 100.000, kelipatan Rp 50.000.
        </p>
      </div>

      {/* Redeem Dialog */}
      {redeemOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" data-testid="redeem-modal">
          <div className="bg-white text-primary rounded-xl w-full max-w-md p-6 relative">
            <button onClick={() => setRedeemOpen(false)} className="absolute top-3 right-3 text-muted-foreground hover:text-primary" data-testid="redeem-close">
              <X className="h-5 w-5" />
            </button>
            <h3 className="font-serif text-2xl">Cairkan Saldo Referral</h3>
            <p className="text-xs text-muted-foreground mt-1">Saldo tersedia: <span className="text-accent font-medium">{fmtRp(data.credit_balance)}</span></p>

            <form onSubmit={submitRedeem} className="mt-5 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Nominal</label>
                <Input
                  type="text"
                  value={redeemForm.amount}
                  onChange={(e) => setRedeemForm({ ...redeemForm, amount: e.target.value.replace(/\D/g, "") })}
                  placeholder="500000"
                  required
                  data-testid="redeem-amount"
                  className="mt-1 font-mono"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Min. Rp 100.000, kelipatan Rp 50.000</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Nama Bank</label>
                <Input
                  value={redeemForm.bank_name}
                  onChange={(e) => setRedeemForm({ ...redeemForm, bank_name: e.target.value })}
                  placeholder="BCA / BRI / Mandiri / …"
                  required
                  data-testid="redeem-bank"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">No. Rekening</label>
                <Input
                  value={redeemForm.bank_account_number}
                  onChange={(e) => setRedeemForm({ ...redeemForm, bank_account_number: e.target.value })}
                  required
                  data-testid="redeem-rek"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Atas Nama</label>
                <Input
                  value={redeemForm.bank_account_name}
                  onChange={(e) => setRedeemForm({ ...redeemForm, bank_account_name: e.target.value })}
                  required
                  data-testid="redeem-name"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Catatan (opsional)</label>
                <Textarea
                  value={redeemForm.notes}
                  onChange={(e) => setRedeemForm({ ...redeemForm, notes: e.target.value })}
                  rows={2}
                  data-testid="redeem-notes"
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={redeeming}
                data-testid="redeem-submit"
                className="w-full inline-flex items-center gap-2"
              >
                {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Kirim Permintaan Pencairan
              </Button>
              <p className="text-[10px] text-muted-foreground text-center mt-2">
                Admin akan memverifikasi dan mentransfer dalam 1-3 hari kerja.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
